import { z } from "zod";
import {
	eventAttendanceLogIdSchema,
	eventAttendanceLogSchema,
} from "./eventAttendance.schemas";
import {
	createEventCancellationPolicySchema,
	eventCancellationPolicyIdSchema,
	updateEventCancellationPolicySchema,
} from "./eventCancellationPolicy.schemas";
import {
	createEventCategorySchema,
	eventCategoryIdSchema,
	updateEventCategorySchema,
} from "./eventCategory.schemas";
import {
	createEventOccasionSchema,
	eventOccasionIdSchema,
	updateEventOccasionSchema,
} from "./eventOccasion.schemas";
import {
	createEventOccurrenceSchema,
	eventOccurrenceIdSchema,
	updateEventOccurrenceSchema,
} from "./eventOccurrence.schemas";
import {
	eventRegistrationIdSchema,
	eventRegistrationSchema,
} from "./eventRegistration.schemas";

export const adminPaginationSchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	pageSize: z.coerce.number().int().min(1).max(200).default(20),
});

export const adminCategoryListQuerySchema = adminPaginationSchema.extend({
	isPublished: z
		.union([z.literal("true"), z.literal("false")])
		.optional()
		.transform((value) => {
			if (value === undefined) return undefined;
			return value === "true";
		}),
	keyword: z.string().max(100).optional(),
});

export const adminCreateCategoryBodySchema = createEventCategorySchema;
export const adminUpdateCategoryBodySchema = updateEventCategorySchema;
export const adminCategoryPathParamsSchema = z.object({
	eventCategoryId: eventCategoryIdSchema,
});

export const adminCreateCancellationPolicyBodySchema =
	createEventCancellationPolicySchema;
export const adminUpdateCancellationPolicyBodySchema =
	updateEventCancellationPolicySchema;
export const adminCancellationPolicyPathParamsSchema = z.object({
	eventCancellationPolicyId: eventCancellationPolicyIdSchema,
});

export const adminOccasionListQuerySchema = adminPaginationSchema.extend({
	eventCategoryId: eventCategoryIdSchema.optional(),
	isPublished: z
		.union([z.literal("true"), z.literal("false")])
		.optional()
		.transform((value) => {
			if (value === undefined) return undefined;
			return value === "true";
		}),
	startDate: z.coerce.date().optional(),
	endDate: z.coerce.date().optional(),
	keyword: z.string().max(100).optional(),
});

export const adminCreateOccasionBodySchema = createEventOccasionSchema;
export const adminUpdateOccasionBodySchema = updateEventOccasionSchema;
export const adminOccasionPathParamsSchema = z.object({
	eventOccasionId: eventOccasionIdSchema,
});

export const adminOccurrenceListQuerySchema = adminPaginationSchema.extend({
	eventOccasionId: eventOccasionIdSchema.optional(),
	startDate: z.coerce.date().optional(),
	endDate: z.coerce.date().optional(),
	isDisplayed: z
		.union([z.literal("true"), z.literal("false")])
		.optional()
		.transform((value) => {
			if (value === undefined) return undefined;
			return value === "true";
		}),
});

export const adminCreateOccurrenceBodySchema = createEventOccurrenceSchema;
export const adminUpdateOccurrenceBodySchema = updateEventOccurrenceSchema;
export const adminOccurrencePathParamsSchema = z.object({
	eventOccurrenceId: eventOccurrenceIdSchema,
});

export const adminBulkCreateOccurrenceBodySchema = z.object({
	eventOccasionId: eventOccasionIdSchema,
	timezone: z.string().min(1).optional(),
	templateSlots: z
		.array(
			z.object({
				startAt: z.coerce.date(),
				endAt: z.coerce.date(),
				maxAttendees: z.coerce.number().int().positive(),
				minAttendees: z.coerce.number().int().min(0).optional(),
				bufferWaitlistSize: z.coerce.number().int().min(0).optional(),
				isDisplayed: z.boolean().optional(),
			}),
		)
		.min(1)
		.max(200),
});

export const adminRegistrationsQuerySchema = adminPaginationSchema.extend({
	eventOccurrenceId: eventOccurrenceIdSchema.optional(),
	eventOccasionId: eventOccasionIdSchema.optional(),
	memberId: z.coerce.number().int().positive().optional(),
	status: z
		.union([
			z.literal("pending"),
			z.literal("confirmed"),
			z.literal("cancelled"),
			z.literal("no_show"),
		])
		.optional(),
	includeCancelled: z
		.union([z.literal("true"), z.literal("false")])
		.optional()
		.transform((value) => {
			if (value === undefined) return false;
			return value === "true";
		}),
	exportFormat: z.enum(["json", "csv"]).default("json").optional(),
});

export const adminExportRegistrationsQuerySchema = z.object({
	eventOccasionId: eventOccasionIdSchema,
	startDate: z.string().optional(), // YYYY-MM-DD
	endDate: z.string().optional(), // YYYY-MM-DD
	format: z.enum(["csv", "excel"]).default("csv"),
});

export const adminRegistrationPathParamsSchema = z.object({
	eventRegistrationId: eventRegistrationIdSchema,
});

export const adminUpdateRegistrationStatusBodySchema = z.object({
	status: z
		.enum(["confirmed", "cancelled", "no_show"], {
			message: "Invalid registration status update",
		})
		.describe("New status to apply to the registration"),
	cancelReason: z.string().max(1000).optional(),
	paymentStatus: z.string().optional(),
	paymentMethod: z.string().optional(),
});

export const adminAttendanceUpdateBodySchema = z.object({
	primaryParticipantCount: z.coerce.number().int().min(0),
	companionCount: z.coerce.number().int().min(0),
	checkedInBy: z.coerce.number().int().positive().optional(),
	checkInSource: z.enum(["barcode", "manual", "migration"]).optional(),
	checkedInAt: z.coerce.date().optional(),
});

export const adminAttendanceLogQuerySchema = adminPaginationSchema.extend({
	eventOccurrenceId: eventOccurrenceIdSchema.optional(),
	eventRegistrationId: eventRegistrationIdSchema.optional(),
	from: z.coerce.date().optional(),
	to: z.coerce.date().optional(),
});

export const adminAttendanceLogPathParamsSchema = z.object({
	eventAttendanceLogId: eventAttendanceLogIdSchema,
});

export const adminEventRegistrationResponseSchema = eventRegistrationSchema;
export const adminEventAttendanceLogResponseSchema = eventAttendanceLogSchema;

export const adminUpdateOccasionsOrderBodySchema = z.object({
	occasions: z.array(
		z.object({
			eventOccasionId: eventOccasionIdSchema,
			displayOrder: z.coerce.number().int().min(0),
		}),
	),
});
